/**
 * Opens a modal, closing any currently open modal first
 * Keeps the overlay visible during transition
 * @param {string} modalId - The ID of the modal to open
 */
window.openModalWithTransition = function(modalId) {
  const currentModal = document.querySelector('dialog-component.header-menu-modal dialog[open]')?.closest('dialog-component');
  const block = document.getElementById(modalId);

  if(!block) return;

  if (block.showDialog) {
    if (currentModal && currentModal !== block) {
      // Open new modal first to keep overlay visible
      block.showDialog();
      
      // Then close the old modal after a short delay
      setTimeout(() => {
        currentModal.closeDialog();
      }, 100);
    } else {
      block.showDialog();
    }

    return;
  }

  
  currentModal.closeDialog();

  setTimeout(() => {
    block.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 400); 
};

/**
 * Initializes header menu modal interactions
 * Handles closing modal on handle and menu item clicks
 */
(function() {
  const initModal = (modalId) => {
    const modalComponent = document.getElementById(modalId);
    if (!modalComponent) return;

    const handle = modalComponent.querySelector('.modal-handle');
    const menuItems = modalComponent.querySelectorAll('.header-menu-modal__item');

    // Close on handle click
    if (handle) {
      handle.addEventListener('click', () => {
        modalComponent.closeDialog();
      });
    }

    // Close on menu item click (but not for modal links)
    menuItems.forEach(item => {
      item.addEventListener('click', (e) => {
        const href = item.getAttribute('href');
        if (href && href.includes('#')) {
          return;
        }
        modalComponent.closeDialog();
      });
    });
  };

  // Initialize modals on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      document.querySelectorAll('dialog-component.header-menu-modal').forEach(modal => {
        if (modal.id) {
          initModal(modal.id);
        }
      });
    });
  } else {
    // DOM already loaded
    document.querySelectorAll('dialog-component.header-menu-modal').forEach(modal => {
      if (modal.id) {
        initModal(modal.id);
      }
    });
  }

  // Also handle dynamically added modals
  const observer = new MutationObserver(() => {
    document.querySelectorAll('dialog-component.header-menu-modal:not([data-initialized])').forEach(modal => {
      if (modal.id) {
        modal.setAttribute('data-initialized', '');
        initModal(modal.id);
      }
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();

/**
 * Global handler for modal links with hash anchors
 * Intercepts clicks on links like <a href="#what-is-topper"> and opens the corresponding modal
 */
(function() {
  // Prevent duplicate initialization
  if (window.modalLinkHandlerInitialized) return;
  window.modalLinkHandlerInitialized = true;

  const handleModalLink = (event) => {
    // Check for links with href="#modal-id"
    const link = event.target.closest('a[href^="#"]');
    if (link) {
      const href = link.getAttribute('href');
      if (!href || !href.startsWith('#')) return;

      const modalId = href.substring(1);
      if (!modalId) return;

      const modal = document.getElementById(modalId);
      if (!modal || !modal.classList.contains('header-menu-modal')) return;

      event.preventDefault();
      event.stopPropagation();
      if (window.openModalWithTransition) {
        window.openModalWithTransition(modalId);
      }
      return;
    }

    // Check for buttons with onclick that sets location.href to hash
    const button = event.target.closest('button[onclick*="#"]');
    if (button) {
      const onclick = button.getAttribute('onclick');
      if (!onclick) return;

      // Extract hash from onclick like "window.location.href = '#memory-foam'"
      const hashMatch = onclick.match(/#([a-zA-Z0-9-]+)/);
      if (!hashMatch) return;

      const modalId = hashMatch[1];
      const modal = document.getElementById(modalId);
      if (!modal || !modal.classList.contains('header-menu-modal')) return;

      event.preventDefault();
      event.stopPropagation();
      
      // Remove onclick to prevent default behavior
      const originalOnclick = button.getAttribute('onclick');
      button.removeAttribute('onclick');
      
      // Open modal
      if (window.openModalWithTransition) {
        window.openModalWithTransition(modalId);
      }
      
      // Restore onclick after a short delay (in case it's needed for other purposes)
      setTimeout(() => {
        if (originalOnclick && !button.hasAttribute('onclick')) {
          button.setAttribute('onclick', originalOnclick);
        }
      }, 100);
    }
  };

  // Add click listener to document to handle all modal links
  document.addEventListener('click', handleModalLink);

  // Also handle hash changes in URL (for direct navigation or browser back/forward)
  const handleHashChange = () => {
    const hash = window.location.hash;
    if (!hash) return;

    const modalId = hash.substring(1);
    const modal = document.getElementById(modalId);
    
    if (modal && modal.classList.contains('header-menu-modal')) {
      // Always try to open, openModalWithTransition will handle if already open
      if (window.openModalWithTransition) {
        window.openModalWithTransition(modalId);
      }
    }
  };

  // Check hash on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handleHashChange);
  } else {
    handleHashChange();
  }

  // Listen for hash changes
  window.addEventListener('hashchange', handleHashChange);
})();

/**
 * Opens a sub-modal (nested modal that can only open inside a parent modal)
 * @param {string} subModalId - The ID of the sub-modal to open
 */
window.openSubModal = function(subModalId) {
  // Check if we're inside a parent modal
  const parentModal = document.querySelector('dialog-component.header-menu-modal dialog[open]')?.closest('dialog-component');
  if (!parentModal) {
    console.warn('Sub-modal can only be opened inside a parent modal');
    return;
  }

  const subModal = document.getElementById(subModalId);
  if (!subModal || !subModal.classList.contains('sub-modal')) return;

  subModal.showDialog();
};

/**
 * Global handler for sub-modal links
 * Only works when clicked inside an open parent modal
 */
(function() {
  if (window.subModalLinkHandlerInitialized) return;
  window.subModalLinkHandlerInitialized = true;

  const handleSubModalLink = (event) => {
    // Check if we're inside a parent modal
    const parentModal = event.target.closest('dialog-component.header-menu-modal dialog[open]')?.closest('dialog-component');
    if (!parentModal) return;

    const link = event.target.closest('a');
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href || !href.startsWith('#')) return;

    const subModalId = href.substring(1);
    if (!subModalId) return;

    const subModal = document.getElementById(subModalId);
    if (!subModal || !subModal.classList.contains('sub-modal')) return;

    event.preventDefault();

    if (window.openSubModal) {
      window.openSubModal(subModalId);
    }
  };

  document.addEventListener('click', handleSubModalLink);
})();


